import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY, ALL_UNITS_QUERY, CREATE_INVENTORY_ITEM_MUTATION } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';

interface InventoryItem {
  id: number;
  name: string;
  inventoryType: string;
  costCents: number;
  archivedAt: string | null;
}

interface Unit {
  id: number;
  name: string;
  abbreviation: string;
}

interface NewInventoryOption {
  inputValue: string;
  name: string;
  isNew: true;
}

type InventoryOption = InventoryItem | NewInventoryOption;

const filterOptions = createFilterOptions<InventoryOption>();

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// Duvet — per Quoting_markdown/duvets.md: Fabrication and Yardage are fixed
// by bed size × Self Welt (see DUVET_TABLE, sopCalculations.ts). Down
// Alternative Insert is an optional opt-in line, priced by bed size
// (DUVET_INSERT_TABLE) — not always needed, so it's a plain toggle.
//
// Unlike most item types, this form's values are submitted through dedicated
// addDuvetDraftQuoteObject/updateDuvetDraftQuoteObject mutations (see
// DraftQuoteDetail.tsx's DEDICATED_TYPE_CONFIG/handleAddObject) rather than
// the generic addDraftQuoteObject/updateDraftQuoteObject every
// non-dedicated-resolver type shares.
const DuvetForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;
  const [bedSize, setBedSize] = useState<'twin' | 'full' | 'queen' | 'king' | ''>((iv?.bedSize as any) || '');
  const [selfWelt, setSelfWelt] = useState<boolean>(iv?.weltType === 'self');
  const [duvetInsert, setDuvetInsert] = useState<boolean>(!!iv?.duvetInsert);
  const [inventoryItemId, setInventoryItemId] = useState(iv?.inventoryItemId != null ? String(iv.inventoryItemId) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [error, setError] = useState<string | null>(null);
  const [autoFilledDescription, setAutoFilledDescription] = useState<string | null>(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [pendingCost, setPendingCost] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data, refetch } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
  });
  const { data: unitsData } = useQuery(ALL_UNITS_QUERY);
  const [createInventoryItem] = useMutation(CREATE_INVENTORY_ITEM_MUTATION);

  const inventoryItems: InventoryItem[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: InventoryItem) => !i.archivedAt && i.inventoryType === 'FABRIC');
  const selectedInventoryItem = inventoryItems.find((i) => String(i.id) === inventoryItemId) || null;

  const units: Unit[] = unitsData?.allUnits?.nodes || [];
  const yardUnit = units.find((u) => /yard/i.test(u.name) || /^yd/i.test(u.abbreviation));

  useEffect(() => {
    if (autoFilledDescription === null && selectedInventoryItem && fabricDescription.trim() === selectedInventoryItem.name) {
      setAutoFilledDescription(selectedInventoryItem.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventoryItem]);

  const selectInventoryItem = (item: InventoryItem) => {
    setInventoryItemId(String(item.id));
    setFabricDescription((prev) => {
      const isBlank = !prev.trim();
      const matchesPreviousAutoFill = autoFilledDescription !== null && prev === autoFilledDescription;
      return isBlank || matchesPreviousAutoFill ? item.name : prev;
    });
    setAutoFilledDescription(item.name);
  };

  const openCreateDialog = (name: string) => {
    setPendingName(name);
    setPendingCost('');
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCreateConfirm = async () => {
    if (!user?.domain_id || !pendingName.trim()) return;
    if (!yardUnit) {
      setCreateError('No "Yards" unit found — add one in Settings first.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const costCents = Math.round((parseFloat(pendingCost) || 0) * 100);
      const result = await createInventoryItem({
        variables: {
          domainId: user.domain_id,
          name: pendingName.trim(),
          inventoryType: 'FABRIC',
          unitId: yardUnit.id,
          costCents,
          description: null,
          widthInches: null,
          quantityOnHand: 0,
        },
      });
      const newItem = result.data?.createInventoryItem?.inventoryItem;
      await refetch();
      if (newItem?.id) {
        const newName = pendingName.trim();
        setInventoryItemId(String(newItem.id));
        setFabricDescription((prev) => {
          const isBlank = !prev.trim();
          const matchesPreviousAutoFill = autoFilledDescription !== null && prev === autoFilledDescription;
          return isBlank || matchesPreviousAutoFill ? newName : prev;
        });
        setAutoFilledDescription(newName);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create inventory item');
    } finally {
      setCreating(false);
    }
  };

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!bedSize) {
        setError('Bed size is required');
        return null;
      }
      setError(null);
      return {
        bedSize,
        weltType: selfWelt ? 'self' : 'none',
        duvetInsert,
        inventoryItemId: inventoryItemId ? parseInt(inventoryItemId, 10) : null,
        fabricDescription: fabricDescription.trim() || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Bed Size</label>
          <select
            className="input"
            value={bedSize}
            onChange={(e) => setBedSize(e.target.value as 'twin' | 'full' | 'queen' | 'king' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="twin">Twin</option>
            <option value="full">Full</option>
            <option value="queen">Queen</option>
            <option value="king">King</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Self Welt</label>
          <select
            className="input"
            value={selfWelt ? 'yes' : 'no'}
            onChange={(e) => setSelfWelt(e.target.value === 'yes')}
            disabled={props.disabled}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Down Alternative Insert <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <select
            className="input"
            value={duvetInsert ? 'yes' : 'no'}
            onChange={(e) => setDuvetInsert(e.target.value === 'yes')}
            disabled={props.disabled}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label>Inventory Item <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <Autocomplete<InventoryOption>
            size="small"
            disabled={props.disabled}
            options={inventoryItems}
            value={selectedInventoryItem}
            isOptionEqualToValue={(option, value) => (
              !('isNew' in option) && !('isNew' in value) && option.id === value.id
            )}
            getOptionLabel={(option) => (
              typeof option === 'string' ? option : ('isNew' in option ? option.inputValue : option.name)
            )}
            filterOptions={(options, params) => {
              const filtered = filterOptions(options, params);
              const inputValue = params.inputValue.trim();
              const exists = options.some((o) => !('isNew' in o) && o.name.toLowerCase() === inputValue.toLowerCase());
              if (inputValue !== '' && !exists) {
                filtered.push({ inputValue, name: inputValue, isNew: true });
              }
              return filtered;
            }}
            renderOption={(liProps, option) => (
              <li {...liProps} key={'isNew' in option ? '__new__' : option.id}>
                {'isNew' in option ? `+ Add "${option.inputValue}" as a new fabric` : option.name}
              </li>
            )}
            onChange={(_event, newValue) => {
              if (newValue == null) {
                setInventoryItemId('');
              } else if ('isNew' in newValue) {
                openCreateDialog(newValue.inputValue);
              } else {
                selectInventoryItem(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search fabric..." />
            )}
          />
        </div>
        {selectedInventoryItem && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Price</label>
            <div style={{ padding: '9px 0', fontSize: 14, color: '#111827' }}>
              {formatCost(selectedInventoryItem.costCents)}
            </div>
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="text"
            className="input"
            placeholder="e.g., name/color"
            value={fabricDescription}
            onChange={(e) => setFabricDescription(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {createDialogOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, width: 420, maxWidth: '90%', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New Fabric</h2>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>
                &ldquo;{pendingName}&rdquo; isn&apos;t in the inventory yet. Add it as a new fabric?
              </p>
            </div>

            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {createError && (
                <div style={{ padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: 13 }}>
                  {createError}
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Name
                </label>
                <input
                  type="text"
                  className="input"
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  disabled={creating}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Cost per Yard ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  placeholder="0.00"
                  value={pendingCost}
                  onChange={(e) => setPendingCost(e.target.value)}
                  disabled={creating}
                />
              </div>

              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                Type: Fabric · Unit: {yardUnit ? yardUnit.name : 'Yards (not found)'} · Qty on hand: 0
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
              <button
                style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={() => setCreateDialogOpen(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={handleCreateConfirm}
                disabled={creating || !pendingName.trim()}
              >
                {creating ? 'Creating...' : 'Create Fabric'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DuvetForm;
